import {
	Attachment,
	AttachmentStatus,
	AttachmentType,
	type FileAttachment,
} from '@/states/attachment'
import { getImageDimensions, getVideoMetadata } from '@/utils/media'
import uploadFile, { CDNFileType } from '@/utils/upload'
import { cloneDeep, isEmpty } from 'lodash-es'
import toast from 'react-hot-toast'
import { useDebounce, useUnmount } from 'react-use'
import { filterFileAttachments, filterWaitToUploadAttachments } from './utils'

interface AttachmentsUploadServiceProps {
	data: Attachment[]
	onUpdate?: (index: number, attachment: Attachment) => void
}

export default function AttachmentsUploadService({
	data,
	onUpdate,
}: AttachmentsUploadServiceProps) {
	const [isReady, cancel] = useDebounce(
		() => {
			const waitToUploadAttachments = filterWaitToUploadAttachments(filterFileAttachments(data))
			if (isEmpty(waitToUploadAttachments)) {
				return
			}

			const uploadAttachment = async (attachment: FileAttachment, index: number) => {
				// set metadata
				if (attachment.type === AttachmentType.PICTURE) {
					const { width, height } = await getImageDimensions(attachment.file)

					attachment.metadata = { width, height }
					attachment.status = AttachmentStatus.UPLOADING
					onUpdate?.(index, attachment)
					const cdnFile = await uploadFile(attachment.file, {
						provisionConfig: {
							uploadType: CDNFileType.PIC,
							bucket: 'jike',
						},
					})

					return cdnFile
				} else if (attachment.type === AttachmentType.VIDEO) {
					const { width, height, url } = await getVideoMetadata(attachment.file)

					attachment.metadata = { width, height }
					attachment.status = AttachmentStatus.UPLOADING
					onUpdate?.(index, attachment)
					const cdnFile = await uploadFile(attachment.file, {
						provisionConfig: {
							uploadType: CDNFileType.VIDEO,
							bucket: 'videos',
						},
					})

					return { ...cdnFile, fileUrl: url }
				}

				throw new Error('Unknown attachment type')
			}
			const UPLOADING_TOAST_ID = 'UPLOADING_TOAST_ID'
			toast.loading(`(0/${waitToUploadAttachments.length}) 正在上传`, {
				id: UPLOADING_TOAST_ID,
			})
			let uploadedCount = 0

			Promise.all(
				waitToUploadAttachments.entries().map(async ([index, attachment]) => {
					const clonedAttachment = cloneDeep(attachment)
					return uploadAttachment(clonedAttachment, index)
						.then((cdnFile) => {
							clonedAttachment.status = AttachmentStatus.UPLOADED
							clonedAttachment.cdn = {
								file: cdnFile,
							}
							onUpdate?.(index, clonedAttachment)
							uploadedCount++
							toast.loading(`(${uploadedCount}/${waitToUploadAttachments.length}) 正在上传`, {
								id: UPLOADING_TOAST_ID,
							})
						})
						.catch(() => {
							clonedAttachment.status = AttachmentStatus.UPLOAD_FAILED
							onUpdate?.(index, clonedAttachment)
						})
				})
			).then(() => {
				toast.dismiss(UPLOADING_TOAST_ID)
				if (uploadedCount === waitToUploadAttachments.length) {
					toast.success('上传完成')
				} else if (uploadedCount === 0) {
					toast.error('上传失败')
				} else {
					toast.error(`部分上传成功，失败${waitToUploadAttachments.length - uploadedCount}个`)
				}
			})
		},
		300,
		[data.length]
	)

	useUnmount(cancel)

	return null
}
